import { useQueryClient } from "@tanstack/react-query";
import {
  usePostLinks,
  usePatchLinksIdFavorite,
  useDeleteLinksId,
} from "../api/generated/links/links";
import { useUpdateLink } from "./useUpdateLink";
import { posthog, isPostHogConfigured } from "../lib/posthog";
import type { LinkItemData } from "../components";

interface UseLinkActionsProps {
  onLinkCreated?: () => void;
  onLinkDeleted?: () => void;
}

interface UseLinkActionsReturn {
  // Mutations
  createLinkMutation: ReturnType<typeof usePostLinks>;
  toggleFavoriteMutation: ReturnType<typeof usePatchLinksIdFavorite>;
  deleteLinkMutation: ReturnType<typeof useDeleteLinksId>;
  updateLinkMutation: ReturnType<typeof useUpdateLink>;
  
  // Handlers
  handleAddLink: (url: string, contextGroupId?: string | null) => void;
  handleFavoriteToggle: (link: LinkItemData) => void;
  handleDeleteLink: (linkId: string) => void;
  handleUpdateLink: (id: string, data: { customTitle?: string | null; tags?: string[] }) => Promise<void>;
}

/**
 * Hook to manage all link-related actions (create, update, delete, favorite)
 * Includes PostHog tracking for all actions
 */
export const useLinkActions = ({ onLinkCreated, onLinkDeleted }: UseLinkActionsProps = {}): UseLinkActionsReturn => {
  const queryClient = useQueryClient();

  // Create Link Mutation
  const createLinkMutation = usePostLinks({
    mutation: {
      onSuccess: () => {
        // Invalida TODAS as queries de links (com qualquer parâmetro)
        queryClient.invalidateQueries({ 
          predicate: (query) => 
            Array.isArray(query.queryKey) && 
            (query.queryKey[0] === "/links" || query.queryKey[0] === "links" || query.queryKey[0] === "/links/search")
        });
        
        if (isPostHogConfigured()) {
          posthog.capture("link_created");
        }
        
        onLinkCreated?.();
      },
      onError: (error: Error) => {
        if (isPostHogConfigured()) {
          posthog.capture("link_create_failed", {
            error: error.message,
          });
        }
      },
    },
  });

  // Toggle Favorite Mutation with Optimistic Updates
  const toggleFavoriteMutation = usePatchLinksIdFavorite({
    mutation: {
      // Optimistic update: atualiza UI IMEDIATAMENTE antes da API responder
      onMutate: async ({ id }) => {
        // 1. Cancela TODAS as queries de links (com qualquer parâmetro)
        await queryClient.cancelQueries({ predicate: (query) => 
          Array.isArray(query.queryKey) && 
          (query.queryKey[0] === "/links" || query.queryKey[0] === "links" || query.queryKey[0] === "/links/search")
        });
        
        // 2. Salva snapshot de TODAS as queries de links para rollback
        const previousQueries = queryClient.getQueriesData({ 
          predicate: (query) => 
            Array.isArray(query.queryKey) && 
            (query.queryKey[0] === "/links" || query.queryKey[0] === "links" || query.queryKey[0] === "/links/search")
        });
        
        // 3. Função helper para atualizar um link no cache
        const updateLinkInCache = (old: any) => {
          if (!old?.data) return old;
          
          // Cria novo array com link atualizado
          const updatedData = old.data.map((link: any) =>
            link.id === id ? { ...link, isFavorite: !link.isFavorite } : link
          );
          
          // Retorna novo objeto para forçar re-render
          return {
            ...old,
            data: updatedData,
          };
        };
        
        // 4. Atualiza TODAS as queries de links (lista e search) com qualquer parâmetro
        queryClient.setQueriesData(
          { 
            predicate: (query) => 
              Array.isArray(query.queryKey) && 
              (query.queryKey[0] === "/links" || query.queryKey[0] === "links" || query.queryKey[0] === "/links/search")
          },
          updateLinkInCache
        );
        
        // 5. Retorna context para rollback
        return { previousQueries };
      },
      
      onSuccess: () => {
        // Tracking de sucesso
        if (isPostHogConfigured()) {
          posthog.capture("link_favorited");
        }
      },
      
      onError: (error: Error, _variables, context: any) => {
        // Rollback: restaura TODOS os snapshots anteriores
        if (context?.previousQueries) {
          context.previousQueries.forEach(([queryKey, data]: any) => {
            queryClient.setQueryData(queryKey, data);
          });
        }
        
        // Tracking de erro
        if (isPostHogConfigured()) {
          posthog.capture("link_favorite_failed", {
            error: error.message,
          });
        }
      },
      
      // Garante sincronização após conclusão (sucesso ou erro)
      onSettled: () => {
        // Invalida TODAS as queries de links (com qualquer parâmetro)
        queryClient.invalidateQueries({ 
          predicate: (query) => 
            Array.isArray(query.queryKey) && 
            (query.queryKey[0] === "/links" || query.queryKey[0] === "links" || query.queryKey[0] === "/links/search")
        });
      },
    },
  });

  // Delete Link Mutation
  const deleteLinkMutation = useDeleteLinksId({
    mutation: {
      onSuccess: () => {
        // Invalida TODAS as queries de links (com qualquer parâmetro)
        queryClient.invalidateQueries({ 
          predicate: (query) => 
            Array.isArray(query.queryKey) && 
            (query.queryKey[0] === "/links" || query.queryKey[0] === "links" || query.queryKey[0] === "/links/search")
        });
        
        if (isPostHogConfigured()) {
          posthog.capture("link_deleted");
        }
        
        onLinkDeleted?.();
      },
      onError: (error: Error) => {
        if (isPostHogConfigured()) {
          posthog.capture("link_delete_failed", {
            error: error.message,
          });
        }
      },
    },
  });

  // Update Link Mutation
  const updateLinkMutation = useUpdateLink();

  // Handlers
  const handleAddLink = (url: string, contextGroupId?: string | null) => {
    createLinkMutation.mutate({ data: { url, ...(contextGroupId && { contextGroupId }) } as { url: string } });
  };

  const handleFavoriteToggle = (link: LinkItemData) => {
    toggleFavoriteMutation.mutate({ id: link.id });
  };

  const handleDeleteLink = (linkId: string) => {
    deleteLinkMutation.mutate({ id: linkId });
  };

  const handleUpdateLink = async (
    id: string,
    data: { customTitle?: string | null; tags?: string[] }
  ) => {
    try {
      await updateLinkMutation.mutateAsync({
        id,
        customTitle: data.customTitle ?? undefined,
        tags: data.tags,
      });

      if (isPostHogConfigured()) {
        posthog.capture("link_updated", {
          hasCustomTitle: Boolean(data.customTitle),
          hasTags: Boolean(data.tags?.length),
        });
      }
    } catch (error) {
      if (isPostHogConfigured()) {
        posthog.capture("link_update_failed", {
          error: error instanceof Error ? error.message : "Unknown error",
        });
      }
      throw error;
    }
  };

  return {
    createLinkMutation,
    toggleFavoriteMutation,
    deleteLinkMutation,
    updateLinkMutation,
    handleAddLink,
    handleFavoriteToggle,
    handleDeleteLink,
    handleUpdateLink,
  };
};

